import React, { useState, useEffect } from 'react';
import { useLoginContext } from '../context/LoginContext';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { toast, Toaster } from 'react-hot-toast';
import {
  Users,
  Plus,
  Edit as EditIcon,
  Trash2 as TrashIcon,
  Shield,
  Code,
  User,
  Clock,
  Mail,
  X,
  Search,
  Globe
} from 'lucide-react';
import {
  fetchTeamMembers,
} from '../utils/apiWrapper';
import { apiRequest } from '../utils/api';

/* ────────────────────────────── Styled Components ────────────────────────────── */

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding: 1rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.375rem 0;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.025em;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 0.8125rem;
  box-shadow: 0 1px 2px rgba(59, 130, 246, 0.3);
  line-height: 1.2;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.875rem;
    font-size: 0.75rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 1.25rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.color || '#3b82f6'};
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StatNumber = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.25rem;
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const StatIcon = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  background: ${props => props.color || '#3b82f6'}15;
  color: ${props => props.color || '#3b82f6'};
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 1.75rem;
    height: 1.75rem;
    top: 0.875rem;
    right: 0.875rem;
  }
`;

const TeamMemberCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 1.25rem;
  border-radius: 0.75rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 0.625rem;
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const MemberInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex: 1;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const Avatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.025em;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
  }
`;

const MemberDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
`;

const MemberName = styled.p`
  margin: 0;
  font-weight: 600;
  font-size: 0.875rem;
  color: #1e293b;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const MemberEmail = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  line-height: 1.3;
  
  svg {
    width: 0.75rem;
    height: 0.75rem;
    min-width: 0.75rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const MemberTimezone = styled.p`
  margin: 0;
  font-size: 0.6875rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  line-height: 1.3;
  
  svg {
    width: 0.6875rem;
    height: 0.6875rem;
    min-width: 0.6875rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.625rem;
  }
`;

const MemberActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    justify-content: flex-end;
    width: 100%;
  }
`;

const RoleBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: capitalize;
  background: ${props => {
    switch (props.role) {
      case 'admin': return '#fef3c7';
      case 'developer': return '#dbeafe';
      case 'member': return '#f3f4f6';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.role) {
      case 'admin': return '#92400e';
      case 'developer': return '#1e40af';
      case 'member': return '#374151';
      default: return '#374151';
    }
  }};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  line-height: 1.2;
  
  svg {
    width: 0.6875rem;
    height: 0.6875rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.625rem;
    padding: 0.1875rem 0.375rem;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f1f5f9;
    transform: scale(1.05);
  }

  &.danger:hover {
    background: #fef2f2;
  }
  
  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.3125rem;
    
    svg {
      width: 0.75rem;
      height: 0.75rem;
    }
  }
`;

/* ─────────── Modal Components ─────────── */

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ show }) => (show ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-width: 450px;
  width: calc(100% - 1.5rem);
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
  border: 1px solid #f1f5f9;

  @keyframes slideUp {
    from { 
      transform: translateY(20px) scale(0.95); 
      opacity: 0; 
    }
    to { 
      transform: translateY(0) scale(1); 
      opacity: 1; 
    }
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    width: calc(100% - 1rem);
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.875rem;
  border-bottom: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: #1e293b;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 0.5rem;
  color: #64748b;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const FormField = styled.div`
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    margin-bottom: 0.875rem;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.625rem;
  border-radius: 0.5rem;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #1e293b;
  font-size: 0.8125rem;
  transition: all 0.2s ease;
  box-sizing: border-box;
  line-height: 1.2;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f3f4f6;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.75rem;
  }
`;

// Add back the Select component
const Select = styled.select`
  width: 100%;
  padding: 0.625rem;
  border-radius: 0.5rem;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #1e293b;
  font-size: 0.8125rem;
  transition: all 0.2s ease;
  cursor: pointer;
  box-sizing: border-box;
  line-height: 1.2;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.75rem;
  }
`;

// New styled components for improved timezone selection
const TimezoneSearchContainer = styled.div`
  position: relative;
  margin-bottom: 0.5rem;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const TimezoneSearchInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.625rem 0.625rem 2.25rem;
  border-radius: 0.5rem;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #1e293b;
  font-size: 0.8125rem;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const TimezoneSelectWrapper = styled.div`
  position: relative;
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: white;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #94a3b8;
    border-radius: 3px;
    
    &:hover {
      background: #64748b;
    }
  }
`;

const TimezoneOption = styled.div`
  padding: 0.625rem 1rem;
  cursor: pointer;
  transition: all 0.15s ease;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #f8fafc;
  }
  
  ${props => props.selected && `
    background: #eff6ff;
    border-left: 3px solid #3b82f6;
    
    &:hover {
      background: #dbeafe;
    }
  `}
  
  &:last-child {
    border-bottom: none;
  }
`;

const TimezoneName = styled.span`
  font-size: 0.8125rem;
  color: #1e293b;
  font-weight: ${props => props.selected ? '500' : '400'};
`;

const TimezoneOffset = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  margin-left: auto;
  white-space: nowrap;
`;

const TimezoneGroup = styled.div`
  margin-top: 0.5rem;
  
  &:first-child {
    margin-top: 0;
  }
`;

const TimezoneGroupLabel = styled.div`
  padding: 0.375rem 0.75rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #64748b;
  background: #f8fafc;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
`;

const PopularBadge = styled.span`
  background: #dbeafe;
  color: #1e40af;
  font-size: 0.625rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  margin-left: 0.5rem;
  text-transform: uppercase;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    margin-top: 1.25rem;
    padding-top: 1rem;
  }
`;

const SecondaryButton = styled.button`
  background: #f8fafc;
  color: #64748b;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #f1f5f9;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.8125rem;
  transition: all 0.2s ease;
  line-height: 1.2;

  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.875rem;
    font-size: 0.75rem;
  }
`;

const DangerButton = styled(PrimaryButton)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 1px 2px rgba(239, 68, 68, 0.3);

  &:hover {
    box-shadow: 0 3px 8px rgba(239, 68, 68, 0.4);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1.5rem;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  border: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const EmptyStateIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  margin: 0 auto 0.875rem;
  background: #f8fafc;
  color: #94a3b8;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
  
  @media (max-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
    margin-bottom: 0.75rem;
    
    svg {
      width: 1.125rem;
      height: 1.125rem;
    }
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.375rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const EmptyStateText = styled.p`
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0 0 1rem;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const ConfirmText = styled.p`
  margin: 0.875rem 0;
  font-size: 0.8125rem;
  color: #64748b;
  text-align: center;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

/* ────────────────────────────── Component ────────────────────────────── */

function TeamsPage() {
  const timezoneOptions = moment.tz.names();
  const { user } = useLoginContext();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isEditMemberModalOpen, setIsEditMemberModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Timezone search states
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [filteredTimezones, setFilteredTimezones] = useState([]);

  // Form states
  const [newMember, setNewMember] = useState({ 
    email: '', 
    name: '', 
    role: 'member', 
    timeZone: 'Asia/Kolkata' // Set default to IST
  });
  const [editMember, setEditMember] = useState({
    email: '',
    name: '',
    role: '',
    permission: '',
    timeZone: ''
  });
  const [memberToDelete, setMemberToDelete] = useState(null);

  // Load team members
  useEffect(() => {
    async function loadTeamMembers() {
      try {
        setLoading(true);
        const members = await fetchTeamMembers();
        setTeamMembers(members || []);
      } catch (error) {
        console.error('Error fetching team members:', error);
        toast.error('Failed to load team members');
      } finally {
        setLoading(false);
      }
    }
    loadTeamMembers();
  }, []);

  // Filter timezones based on search
  useEffect(() => {
    if (timezoneSearch) {
      const searchLower = timezoneSearch.toLowerCase();
      const filtered = timezoneOptions.filter(tz => 
        tz.toLowerCase().includes(searchLower) ||
        getTimezoneOffset(tz).toLowerCase().includes(searchLower)
      );
      setFilteredTimezones(filtered);
    } else {
      setFilteredTimezones(timezoneOptions);
    }
  }, [timezoneSearch]);

  // Calculate statistics
  const teamStats = {
    total: teamMembers.length,
    admins: teamMembers.filter(member => member.role === 'admin').length,
    developers: teamMembers.filter(member => member.role === 'developer').length,
    members: teamMembers.filter(member => member.role === 'member').length,
  };

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield size={12} />;
      case 'developer': return <Code size={12} />;
      default: return <User size={12} />;
    }
  };

  // Get timezone offset
  const getTimezoneOffset = (timezone) => {
    try {
      const now = moment();
      const offset = moment.tz(now, timezone).utcOffset();
      const hours = Math.floor(Math.abs(offset) / 60);
      const minutes = Math.abs(offset) % 60;
      const sign = offset >= 0 ? '+' : '-';
      return `GMT${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  // Format timezone display
  const formatTimezone = (timezone) => {
    try {
      const offset = getTimezoneOffset(timezone);
      return `${timezone} (${offset})`;
    } catch {
      return timezone;
    }
  };

  // Group timezones by region
  const groupTimezonesByRegion = (timezones) => {
    const groups = {};
    timezones.forEach(tz => {
      const region = tz.split('/')[0];
      if (!groups[region]) {
        groups[region] = [];
      }
      groups[region].push(tz);
    });
    return groups;
  };

  // Popular timezones (including IST)
  const popularTimezones = [
    'Asia/Kolkata',
    'America/New_York',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Dubai',
    'Asia/Singapore',
    'Australia/Sydney',
    'Japan',
    'UTC'
  ];

  // Render timezone selector
  const renderTimezoneSelector = (value, onChange) => {
    const groupedTimezones = groupTimezonesByRegion(filteredTimezones);
    
    return (
      <div>
        <TimezoneSearchContainer>
          <SearchIconWrapper>
            <Search size={14} />
          </SearchIconWrapper>
          <TimezoneSearchInput
            type="text"
            placeholder="Search timezone..."
            value={timezoneSearch}
            onChange={(e) => setTimezoneSearch(e.target.value)}
          />
        </TimezoneSearchContainer>
        
        <TimezoneSelectWrapper>
          {/* Popular Timezones Section */}
          {!timezoneSearch && (
            <TimezoneGroup>
              <TimezoneGroupLabel>Popular Timezones</TimezoneGroupLabel>
              {popularTimezones.map(tz => {
                if (timezoneOptions.includes(tz)) {
                  return (
                    <TimezoneOption
                      key={tz}
                      selected={value === tz}
                      onClick={() => onChange(tz)}
                    >
                      <Globe size={14} color="#64748b" />
                      <TimezoneName selected={value === tz}>
                        {tz.replace('_', ' ')}
                      </TimezoneName>
                      {tz === 'Asia/Kolkata' && (
                        <PopularBadge>IST</PopularBadge>
                      )}
                      <TimezoneOffset>
                        {getTimezoneOffset(tz)}
                      </TimezoneOffset>
                    </TimezoneOption>
                  );
                }
                return null;
              })}
            </TimezoneGroup>
          )}
          
          {/* All Timezones by Region */}
          {Object.entries(groupedTimezones).map(([region, tzs]) => (
            <TimezoneGroup key={region}>
              <TimezoneGroupLabel>{region}</TimezoneGroupLabel>
              {tzs.map(tz => (
                <TimezoneOption
                  key={tz}
                  selected={value === tz}
                  onClick={() => onChange(tz)}
                >
                  <Globe size={14} color="#64748b" />
                  <TimezoneName selected={value === tz}>
                    {tz.split('/').slice(1).join('/').replace(/_/g, ' ')}
                  </TimezoneName>
                  <TimezoneOffset>
                    {getTimezoneOffset(tz)}
                  </TimezoneOffset>
                </TimezoneOption>
              ))}
            </TimezoneGroup>
          ))}
          
          {filteredTimezones.length === 0 && (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>
              No timezones found
            </div>
          )}
        </TimezoneSelectWrapper>
      </div>
    );
  };

  /* ───────────────────────── HANDLERS ───────────────────────── */

  const handleAddMember = async () => {
    if (!newMember.name || !newMember.email || !newMember.role) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      const response = await apiRequest('/api/auth/settings/team/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newMember),
      });
      const result = await response.json();

      if (result.success) {
        const newMemberData = {
          ...result.member,
          initials: newMember.name.split(' ').map(n => n[0]).join('').toUpperCase()
        };
        setTeamMembers([...teamMembers, newMemberData]);
        setIsAddMemberModalOpen(false);
        setNewMember({ email: '', name: '', role: 'member', timeZone: 'Asia/Kolkata' });
        setTimezoneSearch('');
        toast.success('Team member added successfully!');
      } else {
        toast.error(result.message || 'Error adding member.');
      }
    } catch (err) {
      toast.error('Error adding member.');
    }
  };

  const handleNewMemberInputChange = (e) => {
    const { name, value } = e.target;
    setNewMember((prev) => ({ ...prev, [name]: value }));
  };

  const handleTimezoneChange = (timezone) => {
    setNewMember(prev => ({ ...prev, timeZone: timezone }));
    setTimezoneSearch('');
  };

  const openEditModal = (member) => {
    setEditMember({
      email: member.email,
      name: member.name,
      role: member.role.toLowerCase() || 'member',
      timeZone: member.timeZone || '',
    });
    setIsEditMemberModalOpen(true);
  };

  const handleEditMemberChange = (e) => {
    const { name, value } = e.target;
    setEditMember((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditTimezoneChange = (timezone) => {
    setEditMember(prev => ({ ...prev, timeZone: timezone }));
    setTimezoneSearch('');
  };

  const handleEditMemberSubmit = async () => {
    if (!editMember.name || !editMember.email || !editMember.role) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      const response = await apiRequest('/api/auth/settings/team/edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editMember),
      });

      if (response.ok) {
        const updatedTeam = teamMembers.map((m) =>
          m.email === editMember.email
            ? { ...m, ...editMember }
            : m
        );
        setTeamMembers(updatedTeam);
        setIsEditMemberModalOpen(false);
        setTimezoneSearch('');
        toast.success('Team member updated successfully!');
      } else {
        toast.error('Error editing member.');
      }
    } catch (err) {
      toast.error('Error editing member.');
    }
  };

  const openDeleteConfirm = (member) => {
    setMemberToDelete(member);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      const response = await apiRequest('/api/auth/settings/team/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: memberToDelete.email }),
      });

      if (response.ok) {
        const filteredTeam = teamMembers.filter(
          (m) => m.email !== memberToDelete.email
        );
        setTeamMembers(filteredTeam);
        setIsDeleteConfirmOpen(false);
        setMemberToDelete(null);
        toast.success('Team member removed successfully!');
      } else {
        toast.error('Error removing member.');
      }
    } catch (err) {
      toast.error('Error removing member.');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem',
          background: 'white',
          borderRadius: '0.75rem',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
          marginTop: '1.5rem'
        }}>
          <div style={{ fontSize: '0.8125rem', color: '#64748b', lineHeight: '1.4' }}>
            Loading team members...
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header Actions */}
      <HeaderActions>
        <PrimaryButton onClick={() => setIsAddMemberModalOpen(true)}>
          <Plus size={14} />
          Add Team Member
        </PrimaryButton>
      </HeaderActions>

      {/* Team Statistics */}
      <StatsContainer>
        <StatCard color="#3b82f6">
          <StatIcon color="#3b82f6">
            <Users size={16} />
          </StatIcon>
          <StatNumber>{teamStats.total}</StatNumber>
          <StatLabel>Total Members</StatLabel>
        </StatCard>
        <StatCard color="#f59e0b">
          <StatIcon color="#f59e0b">
            <Shield size={16} />
          </StatIcon>
          <StatNumber>{teamStats.admins}</StatNumber>
          <StatLabel>Administrators</StatLabel>
        </StatCard>
        <StatCard color="#10b981">
          <StatIcon color="#10b981">
            <Code size={16} />
          </StatIcon>
          <StatNumber>{teamStats.developers}</StatNumber>
          <StatLabel>Developers</StatLabel>
        </StatCard>
        <StatCard color="#6366f1">
          <StatIcon color="#6366f1">
            <User size={16} />
          </StatIcon>
          <StatNumber>{teamStats.members}</StatNumber>
          <StatLabel>Members</StatLabel>
        </StatCard>
      </StatsContainer>

      {/* Team Members List */}
      <div>
        {teamMembers.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <Users size={20} />
            </EmptyStateIcon>
            <EmptyStateTitle>No team members yet</EmptyStateTitle>
            <EmptyStateText>Get started by adding your first team member to your workspace.</EmptyStateText>
          </EmptyState>
        ) : (
          teamMembers.map((member) => (
            <TeamMemberCard key={member.email}>
              <MemberInfoContainer>
                <Avatar>
                  {member.initials || member.name?.[0] || '?'}
                </Avatar>
                <MemberDetails>
                  <MemberName>{member.name}</MemberName>
                  <MemberEmail>
                    <Mail size={12} />
                    {member.email}
                  </MemberEmail>
                  {member.timeZone && (
                    <MemberTimezone>
                      <Clock size={11} />
                      {formatTimezone(member.timeZone)}
                    </MemberTimezone>
                  )}
                </MemberDetails>
              </MemberInfoContainer>
              <MemberActions>
                <RoleBadge role={member.role}>
                  {getRoleIcon(member.role)}
                  {member.role}
                </RoleBadge>
                <ActionButton onClick={() => openEditModal(member)}>
                  <EditIcon size={16} color="#64748b" />
                </ActionButton>
                <ActionButton 
                  className="danger" 
                  onClick={() => openDeleteConfirm(member)}
                >
                  <TrashIcon size={16} color="#ef4444" />
                </ActionButton>
              </MemberActions>
            </TeamMemberCard>
          ))
        )}
      </div>

      {/* Add Member Modal */}
      <ModalOverlay
        show={isAddMemberModalOpen}
        onClick={() => {
          setIsAddMemberModalOpen(false);
          setTimezoneSearch('');
        }}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Add New Team Member</ModalTitle>
            <CloseButton onClick={() => {
              setIsAddMemberModalOpen(false);
              setTimezoneSearch('');
            }}>
              <X size={16} />
            </CloseButton>
          </ModalHeader>
          
          <FormField>
            <Label>Full Name</Label>
            <Input
              type="text"
              name="name"
              placeholder="Enter member's full name"
              value={newMember.name}
              onChange={handleNewMemberInputChange}
            />
          </FormField>

          <FormField>
            <Label>Email Address</Label>
            <Input
              type="email"
              name="email"
              placeholder="Enter member's email"
              value={newMember.email}
              onChange={handleNewMemberInputChange}
            />
          </FormField>

          <FormField>
            <Label>Role</Label>
            <Select
              name="role"
              value={newMember.role}
              onChange={handleNewMemberInputChange}
            >
              <option value="member">Member</option>
              <option value="developer">Developer</option>
              <option value="admin">Administrator</option>
            </Select>
          </FormField>

          <FormField>
            <Label>Timezone</Label>
            {renderTimezoneSelector(newMember.timeZone, handleTimezoneChange)}
          </FormField>

          <ModalActions>
            <SecondaryButton onClick={() => {
              setIsAddMemberModalOpen(false);
              setTimezoneSearch('');
            }}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleAddMember}>
              Add Member
            </PrimaryButton>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>

      {/* Edit Member Modal */}
      <ModalOverlay
        show={isEditMemberModalOpen}
        onClick={() => {
          setIsEditMemberModalOpen(false);
          setTimezoneSearch('');
        }}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Edit Team Member</ModalTitle>
            <CloseButton onClick={() => {
              setIsEditMemberModalOpen(false);
              setTimezoneSearch('');
            }}>
              <X size={16} />
            </CloseButton>
          </ModalHeader>
          
          <FormField>
            <Label>Email Address</Label>
            <Input
              type="email"
              name="email"
              value={editMember.email}
              disabled
            />
          </FormField>

          <FormField>
            <Label>Full Name</Label>
            <Input
              type="text"
              name="name"
              placeholder="Enter member's full name"
              value={editMember.name}
              onChange={handleEditMemberChange}
            />
          </FormField>

          <FormField>
            <Label>Role</Label>
            <Select
              name="role"
              value={editMember.role}
              onChange={handleEditMemberChange}
            >
              <option value="member">Member</option>
              <option value="developer">Developer</option>
              <option value="admin">Administrator</option>
            </Select>
          </FormField>

          <FormField>
            <Label>Timezone</Label>
            {renderTimezoneSelector(editMember.timeZone, handleEditTimezoneChange)}
          </FormField>

          <ModalActions>
            <SecondaryButton onClick={() => {
              setIsEditMemberModalOpen(false);
              setTimezoneSearch('');
            }}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleEditMemberSubmit}>
              Save Changes
            </PrimaryButton>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>

      {/* Delete Confirmation Modal */}
      <ModalOverlay
        show={isDeleteConfirmOpen}
        onClick={() => setIsDeleteConfirmOpen(false)}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Remove Team Member</ModalTitle>
            <CloseButton onClick={() => setIsDeleteConfirmOpen(false)}>
              <X size={16} />
            </CloseButton>
          </ModalHeader>
          
          <ConfirmText>
            Are you sure you want to remove{' '}
            <strong>{memberToDelete?.name}</strong> from your team?
            <br />
            <br />
            This action cannot be undone and they will lose access to all team resources.
          </ConfirmText>

          <ModalActions>
            <SecondaryButton onClick={() => setIsDeleteConfirmOpen(false)}>
              Cancel
            </SecondaryButton>
            <DangerButton onClick={handleDeleteMember}>
              Remove Member
            </DangerButton>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>

      {/* Toast Container */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#1e293b',
            fontSize: '0.8125rem',
            borderRadius: '0.5rem',
            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.15)',
            border: '1px solid #f1f5f9'
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </PageContainer>
  );
}

export default TeamsPage;
